import { useMemo, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { useBuckets } from "../hooks";
import { summariseBuckets, type ClassName, type Period } from "../utils/summarise-buckets";
import { PeriodDropdown } from "./PeriodDropdown.web";

interface ConfigDataSectionProps {
  /** The selected scenario's keys, or null when nothing is selected. */
  selected: {
    siteId: SiteId;
    assetCameraKey: AssetCameraKey;
    scenarioUuid: string;
    scenarioName: string;
  } | null;
}

const CLASS_LABELS: Record<ClassName, string> = {
  car: "Cars",
  human: "People",
  bike: "Bikes",
  bus: "Buses",
  truck: "Trucks",
  otherVehicle: "Other",
};

/**
 * Section 3 of the config page: the scenario traffic data/history, relocated
 * from the old Traffic Flow screen and made **collapsed + lazy** (design doc
 * §4b). It is:
 *   - Collapsed by default and disabled entirely when no scenario is selected.
 *   - Lazy — the inner `ScenarioBuckets` (which owns the `useBuckets` fetch) is
 *     only mounted while expanded, so setup work never pays the data-fetch cost.
 *
 * Data is deliberately de-emphasised here: the operator's job is configuration,
 * not analysis, so this stays out of the way until deliberately opened.
 */
export function ConfigDataSection({ selected }: ConfigDataSectionProps) {
  const { theme } = useUnistyles();
  const [expanded, setExpanded] = useState(false);

  const disabled = !selected;
  // Collapse whenever selection is lost so we don't leave a stale, empty
  // expanded panel; expansion only makes sense with a scenario in context.
  const isOpen = expanded && !disabled;

  return (
    <View
      style={{
        borderRadius: theme.spacing.md,
        borderWidth: 1,
        borderColor: theme.colors.borderCard,
        overflow: "hidden",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Pressable
        onPress={() => setExpanded((v) => !v)}
        disabled={disabled}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingHorizontal: theme.spacing.xl,
          paddingVertical: theme.spacing.lg,
          backgroundColor: theme.colors.surfaceCard,
        }}
      >
        <Text textStyle="sm/semibold" color="text">
          Scenario data
        </Text>
        <Icon
          name={isOpen ? "chevronUp" : "chevronDown"}
          size={theme.spacing["4xl"]}
          color={disabled ? theme.colors.textMuted : theme.colors.icon}
        />
      </Pressable>

      {isOpen && selected ?
        <View style={{ padding: theme.spacing.xl }}>
          <ScenarioBuckets
            siteId={selected.siteId}
            assetCameraKey={selected.assetCameraKey}
            scenarioUuid={selected.scenarioUuid}
          />
        </View>
      : null}
    </View>
  );
}

interface ScenarioBucketsProps {
  siteId: SiteId;
  assetCameraKey: AssetCameraKey;
  scenarioUuid: string;
}

/**
 * The data table itself. Split out so mounting it (only while the section is
 * expanded) is what triggers the `useBuckets` fetch — that's the whole "lazy"
 * mechanism. Mirrors the table in `ScenarioDetailPanel.web.tsx`, minus that
 * component's routing-coupled header/close (collapse is owned by the wrapper).
 */
function ScenarioBuckets({ siteId, assetCameraKey, scenarioUuid }: ScenarioBucketsProps) {
  const { theme } = useUnistyles();
  const [period, setPeriod] = useState<Period>("7d");

  const { data: buckets, isLoading, error } = useBuckets(siteId, assetCameraKey, scenarioUuid, period);
  const summary = useMemo(() => (buckets ? summariseBuckets(buckets, period) : null), [buckets, period]);

  return (
    <View gap="lg">
      <View>
        <PeriodDropdown value={period} onChange={setPeriod} />
      </View>

      {isLoading && (
        <View alignItems="center" justifyContent="center" style={{ paddingVertical: theme.spacing["5xl"] }}>
          <ActivityIndicator />
        </View>
      )}

      {error && !buckets && (
        <View alignItems="center" justifyContent="center" gap="lg" style={{ paddingVertical: theme.spacing["5xl"] }}>
          <Icon name="motion" size={theme.spacing["7xl"]} color={theme.colors.textMuted} />
          <Text textStyle="sm/regular" color="danger">
            Failed to load traffic data
          </Text>
        </View>
      )}

      {summary && summary.rows.every((r) => r.total === 0) && (
        <View
          alignItems="center"
          justifyContent="center"
          style={{
            borderWidth: 1,
            borderColor: theme.colors.borderCard,
            borderRadius: theme.spacing.md,
            padding: theme.spacing["5xl"],
            gap: theme.spacing.lg,
          }}
        >
          <Icon name="motion" size={theme.spacing["7xl"]} color={theme.colors.textMuted} />
          <Text textStyle="sm/regular" color="textMuted" numberOfLines={2} style={{ textAlign: "center" }}>
            This camera hasn&apos;t reported any traffic-flow counts for the selected period.
          </Text>
        </View>
      )}

      {summary && summary.rows.some((r) => r.total > 0) && (
        <ScrollView>
          <View
            style={{
              borderWidth: 1,
              borderColor: theme.colors.borderCard,
              borderRadius: theme.spacing.md,
              overflow: "hidden",
            }}
          >
            <View
              flexDirection="row"
              style={{
                paddingHorizontal: theme.spacing.xl,
                paddingVertical: theme.spacing.lg,
                backgroundColor: theme.colors.surfaceCard,
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.borderCard,
              }}
            >
              <Text textStyle="xs/semibold" color="textMuted" style={{ flex: 2 }}>
                Class
              </Text>
              <Text textStyle="xs/semibold" color="textMuted" style={{ flex: 1, textAlign: "right" }}>
                Total
              </Text>
              <Text textStyle="xs/semibold" color="textMuted" style={{ flex: 1, textAlign: "right" }}>
                Avg/day
              </Text>
              <Text textStyle="xs/semibold" color="textMuted" style={{ flex: 1, textAlign: "right" }}>
                Peak hour
              </Text>
            </View>
            {summary.rows.map((row) => (
              <View
                key={row.class}
                flexDirection="row"
                style={{
                  paddingHorizontal: theme.spacing.xl,
                  paddingVertical: theme.spacing.lg,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.borderCard,
                }}
              >
                <Text textStyle="sm/regular" color="text" style={{ flex: 2 }}>
                  {CLASS_LABELS[row.class]}
                </Text>
                <Text textStyle="sm/regular" color="text" style={{ flex: 1, textAlign: "right" }}>
                  {row.total.toLocaleString()}
                </Text>
                <Text textStyle="sm/regular" color="textMuted" style={{ flex: 1, textAlign: "right" }}>
                  {row.avgPerDay.toLocaleString()}
                </Text>
                <Text textStyle="sm/regular" color="textMuted" style={{ flex: 1, textAlign: "right" }}>
                  {row.peakHour}
                </Text>
              </View>
            ))}
          </View>
        </ScrollView>
      )}
    </View>
  );
}
